import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MessageSquare, ThumbsUp, AlertTriangle, Lightbulb, Trash2, Check } from "lucide-react";

const ICONS = {
  suggestion: Lightbulb,
  complaint: AlertTriangle,
  compliment: ThumbsUp,
};

const COLORS = {
  suggestion: "#7c3aed",
  complaint: "#dc2626",
  compliment: "#16a34a",
};

export default function FeedbackPanel({ client }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "suggestion" || filter === "complaint" || filter === "compliment") {
        params.kind = filter;
      } else if (filter === "open") {
        params.resolved = false;
      } else if (filter === "resolved") {
        params.resolved = true;
      }
      const { data } = await client.get("/admin/feedback", { params });
      setRows(data);
    } catch {
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, [client, filter]);

  useEffect(() => { load(); }, [load]);

  const toggleResolved = async (row) => {
    try {
      await client.patch(`/admin/feedback/${row.id}`, { resolved: !row.resolved });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await client.delete(`/admin/feedback/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = {
    suggestion: rows.filter((r) => r.kind === "suggestion").length,
    complaint: rows.filter((r) => r.kind === "complaint").length,
    compliment: rows.filter((r) => r.kind === "compliment").length,
  };

  return (
    <div className="space-y-4" data-testid="feedback-panel">
      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "suggestion", label: "SUGGESTIONS" },
          { k: "complaint", label: "COMPLAINTS" },
          { k: "compliment", label: "COMPLIMENTS" },
        ].map((c) => {
          const Icon = ICONS[c.k];
          return (
            <div key={c.k} className="border-2 border-slate-900 bg-white p-3 flex items-center gap-3">
              <Icon className="h-6 w-6" style={{ color: COLORS[c.k] }} />
              <div>
                <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500">{c.label}</div>
                <div className="font-heading text-2xl tabular" style={{ color: COLORS[c.k] }}>
                  {counts[c.k]}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 border-2 border-slate-900 bg-white p-3">
        {[
          { k: "all", label: "ALL" },
          { k: "open", label: "OPEN" },
          { k: "resolved", label: "RESOLVED" },
          { k: "suggestion", label: "SUGGESTIONS" },
          { k: "complaint", label: "COMPLAINTS" },
          { k: "compliment", label: "COMPLIMENTS" },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`feedback-filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`border-2 px-3 py-1.5 font-heading text-[10px] tracking-wider ${
              filter === f.k
                ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
            }`}
          >
            {f.label}
          </button>
        ))}
        {loading && (
          <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-slate-400">Loading…</span>
        )}
      </div>

      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="border-2 border-slate-300 bg-white p-8 text-center text-xs uppercase tracking-[0.2em] text-slate-400">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No messages yet
          </div>
        )}
        {rows.map((r) => {
          const Icon = ICONS[r.kind] || MessageSquare;
          return (
            <div
              key={r.id}
              data-testid={`feedback-row-${r.id}`}
              className={`border-2 bg-white p-3 ${r.resolved ? "border-slate-300 opacity-70" : "border-slate-900"}`}
            >
              <div className="flex items-start gap-3">
                <Icon className="h-5 w-5 flex-shrink-0 mt-1" style={{ color: COLORS[r.kind] }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-heading text-[10px] tracking-[0.2em] uppercase"
                      style={{ color: COLORS[r.kind] }}
                    >
                      {r.kind}
                    </span>
                    <span className="text-[10px] text-slate-400 tabular">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                    {r.name && (
                      <span className="text-xs font-medium text-slate-900">· {r.name}</span>
                    )}
                    {r.phone && (
                      <span className="text-[11px] text-slate-500 tabular">{r.phone}</span>
                    )}
                    {r.resolved && (
                      <span className="bg-emerald-600 px-1.5 py-0.5 text-[9px] tracking-[0.2em] text-white font-heading">
                        RESOLVED
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-900 whitespace-pre-wrap break-words">
                    {r.message}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    data-testid={`feedback-resolve-${r.id}-btn`}
                    onClick={() => toggleResolved(r)}
                    className="border border-slate-300 p-1.5 text-slate-500 hover:border-emerald-600 hover:text-emerald-600"
                    title={r.resolved ? "Reopen" : "Mark resolved"}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`feedback-delete-${r.id}-btn`}
                    onClick={() => remove(r.id)}
                    className="border border-slate-300 p-1.5 text-slate-500 hover:border-red-600 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
